import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { AppConfigService, AuthService, NewsService } from '@services';
import { Observable, Subscription, map, tap } from 'rxjs';
import { ECustomMenu } from '../../enums';
import { IConfig } from '../../interfaces';
import { User } from '../../models';
import { AppState } from '../../state';
import { selectBreakpoints, selectUser } from '../../state/session/session.selectors';

/**
 * LayoutComponent
 *
 * @export
 * @class LayoutComponent
 * @typedef {LayoutComponent}
 */
@Component({
  selector: 'aup-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  standalone: false,
})
export class LayoutComponent implements OnInit, OnDestroy {
  @ViewChild('drawer') drawer?: MatSidenav;

  /**
   * Breakpoints observable
   */
  breakpoints$ = this.store.select(selectBreakpoints);

  isMobile = false;
  /**
   * Current logged user
   *
   * @type {Observable<User>}
   */
  user$: Observable<User>;

  isMenuExpanded = true;
  isHandsetLayout = false;
  customMenu?: ECustomMenu;
  eCustomMenu = ECustomMenu;

  subscription: Subscription = new Subscription();
  public appConfig: IConfig;

  // Language-related properties
  public availableLanguages: string[] = [];
  public shouldShowLanguageMenu = false;
  public shouldShowSettingsMenu = false;
  public logoPath: string;
  public pageTitle: string;
  public languageNames: { [key: string]: string } = {
    it: 'Italiano',
    en: 'English',
    pl: 'Polski',
  };

  /**
   * Creates an instance of LayoutComponent.
   *
   * @constructor
   * @param {Store<AppState>} store
   * @param {AuthService} authService
   * @param {AppConfigService} appConfigService
   * @param {Router} router
   * @param {NewsService} newsService
   * @param {TranslocoService} translocoService
   */
  constructor(
    private readonly store: Store<AppState>,
    private readonly authService: AuthService,
    private readonly appConfigService: AppConfigService,
    private readonly router: Router,
    private readonly translocoService: TranslocoService
  ) {
    this.user$ = this.store.select(selectUser);
    this.appConfig = this.appConfigService.getConfig()!;
    this.shouldShowSettingsMenu = this.appConfigService.isSettingsModuleEnabled();

    const countryConfig = this.appConfigService.getCountryConfig();
    this.logoPath = countryConfig?.logoPath;
    this.pageTitle = countryConfig?.pageTitle;
  }

  ngOnInit(): void {
    // Initialize language-related properties
    this.availableLanguages = this.translocoService.getAvailableLangs() as string[];
    this.shouldShowLanguageMenu = this.availableLanguages.length > 1;

    this.store
      .select(selectBreakpoints)
      .pipe(
        map(breakpoints => breakpoints.isXSmall),
        tap(isMobile => (this.isMobile = isMobile))
      )
      .subscribe();

    this.store.select(selectBreakpoints).subscribe(breakpoints => {
      this.isHandsetLayout = breakpoints.isHandset;
    });

    this.listenForCustomMenus();
  }

  onSuggestionClick() {
    const button = document.querySelector('.usabilla-live-button');
    if (button) {
      (button as HTMLIFrameElement).contentWindow?.document.querySelector('html')?.click();
      this.closeDrawerIfMobile();
    }
  }

  closeDrawerIfMobile() {
    if (this.isMobile) {
      this.drawer!.close();
    }
  }
  /**
   * Logout user
   */
  logout() {
    this.authService.logout();
  }

  onCustomMenuOpen() {
    setTimeout(() => {
      this.drawer!.open();
    });
  }

  /**
   * Get current active language
   * @returns {string} Current language code
   */
  getCurrentLanguage(): string {
    return this.translocoService.getActiveLang();
  }

  /**
   * Switch to specified language
   * @param {string} language Language code to switch to
   */
  switchLanguage(language: string): void {
    this.translocoService.setActiveLang(language);
  }

  /**
   * Get language display name
   * @param {string} langCode Language code
   * @returns {string} Display name for the language
   */
  getLanguageDisplayName(langCode: string): string {
    return this.languageNames[langCode] || langCode;
  }

  private listenForCustomMenus() {
    this.subscription.add(
      this.router.events.subscribe(event => {
        let route = this.router.routerState.snapshot.root;

        while (route.firstChild) {
          if (route.firstChild.data['menu']) {
            this.setCustomMenu(route.firstChild.data['menu']);
            return;
          }
          route = route.firstChild;
        }
        this.setCustomMenu(undefined);
      })
    );
  }

  private setCustomMenu(menu: ECustomMenu | undefined) {
    this.customMenu = menu;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
